import type { HttpPort } from "~/lib/http";

export interface SchoolOverview {
  school: {
    name: string;
    status: "trial" | "active" | "expired" | "suspended" | "deleted";
    trialEndsAt: string | null;
    daysLeft: number | null;
    classCount: number;
    classLimit: number | null;
    canInvite: boolean;
  };
  members: Array<{ id: string; firstName: string; lastName: string; email: string; role: "owner" | "teacher"; active: boolean; classCount: number }>;
  invitations: Array<{ id: string; email: string; firstName: string | null; lastName: string | null; expiresAt: string; createdAt: string }>;
}

export interface SchoolClass {
  id: string;
  name: string;
  schoolYear: string | null;
  teacherId: string;
  teacherFirst: string;
  teacherLast: string;
  studentCount: number;
  readers: Array<{ id: string; firstName: string; lastName: string }>;
  pendingRequests: number;
}

export interface ClassRole {
  classId: string;
  className: string;
  role: "edit" | "read";
}

// Welche Klassen eine Lehrkraft in welcher Rolle hat: bearbeitet (edit) oder liest mit (read).
export function classRolesOf(memberId: string, classes: SchoolClass[]): ClassRole[] {
  const roles: ClassRole[] = [];
  for (const value of classes) {
    if (value.teacherId === memberId) roles.push({ classId: value.id, className: value.name, role: "edit" });
    else if (value.readers.some((reader) => reader.id === memberId)) roles.push({ classId: value.id, className: value.name, role: "read" });
  }
  return roles.sort((a, b) => (a.role === b.role ? a.className.localeCompare(b.className, "de") : a.role === "edit" ? -1 : 1));
}

export interface InvitationPreview {
  schoolName: string;
  email: string;
  firstName: string;
  lastName: string;
  inviterName: string | null;
}

// Womit das Dashboard startet: Schulleitung über den Login-Knopf, oder direkt nach Registrierung bzw. Einladung.
export type DashboardEntry = "principal" | "welcome-principal" | "welcome-teacher";

export function createTeamApi(http: HttpPort, token: string) {
  return {
    overview: () => http.request<SchoolOverview>({ path: "/school/overview" }),
    invite: (email: string) => http.request<{ id: string; email: string }>({ path: "/school/invitations", method: "POST", token, body: { email } }),
    revoke: (id: string) => http.request<{ id: string }>({ path: `/school/invitations/${encodeURIComponent(id)}`, method: "DELETE", token }),
    schoolClasses: () => http.request<{ classes: Array<{ id: string; name: string; school_year: string | null; teacher_id: string; teacher_first: string; teacher_last: string; student_count: string; readers?: SchoolClass["readers"]; pending_requests?: number }> }>({ path: "/school/classes" }),
    addReader: (classId: string, userId: string) => http.request<{ success: boolean }>({ path: `/school/classes/${encodeURIComponent(classId)}/readers`, method: "POST", token, body: { userId } }),
    removeReader: (classId: string, userId: string) => http.request<{ success: boolean }>({ path: `/school/classes/${encodeURIComponent(classId)}/readers/${encodeURIComponent(userId)}`, method: "DELETE", token }),
    assignClass: (classId: string, teacherId: string) => http.request<{ success: boolean }>({ path: `/school/classes/${encodeURIComponent(classId)}/assign`, method: "PATCH", token, body: { teacherId } })
  };
}

export type TeamApi = ReturnType<typeof createTeamApi>;

export function createInvitationApi(http: HttpPort) {
  return {
    preview: (token: string) => http.request<InvitationPreview>({ path: "/invitations/preview", method: "POST", body: { token } })
  };
}

export function formatDate(value: string | null | undefined) {
  if (!value) return "–";
  return new Intl.DateTimeFormat("de-DE", { dateStyle: "medium", timeZone: "Europe/Berlin" }).format(new Date(value));
}

export function statusLabel(school: SchoolOverview["school"]) {
  switch (school.status) {
    case "trial": return school.daysLeft === null ? "Test läuft" : school.daysLeft === 1 ? "Test läuft · noch 1 Tag" : `Test läuft · noch ${school.daysLeft} Tage`;
    case "active": return "Lizenz aktiv";
    case "expired": return "Test abgelaufen";
    default: return "Zugang gesperrt";
  }
}

export function classesLabel(school: SchoolOverview["school"]) {
  return school.classLimit === null ? String(school.classCount) : `${school.classCount} von ${school.classLimit}`;
}
